export const meta = {
  title: 'Introducing Cently for iOS: Your Financial Dashboard in Your Pocket',
  date: '2026-03-15',
  description:
    'We\'re bringing Cently to iPhone. Track spending, manage accounts, and chat with your financial data, all from your pocket.',
  metaDescription:
    'Cently launches on iOS with spending analytics, transaction tracking, and AI-powered insights designed for your financial life on the go.',
  keywords: [
    'iOS app',
    'mobile finance',
    'spending tracker',
    'personal finance',
    'financial dashboard',
    'money management',
    'transaction tracking',
    'Plaid integration',
    'AI finance',
  ],
};

_March 15, 2026_

We built Cently because managing your finances shouldn't require sitting at a desk with a spreadsheet open. You're checking your balance at the grocery store. You're wondering if you overspent this month at 9 PM. You're trying to remember which account that last transaction came from.

Today, we're bringing Cently to where you already are: your iPhone.

## Why mobile matters for your finances

The web app works great when you're at your desk planning. But your real financial life happens in the real world. You're making purchase decisions, tracking spending habits, and remembering transactions when you're anywhere but at a computer.

Most finance apps treat mobile as an afterthought: a stripped-down version of the web app with fewer features. We built Cently for iOS differently. We designed it from the ground up for the moments that matter: the split-second decision at checkout, the quiet moment before bed when you want to see how your month's going, the conversation with your financial data.

## What you get with Cently on iOS

![Cently for iOS dashboard on iPhone](/blog/images/cently-ios-app-launch.png)

**Your spending at a glance**

Open the app and see exactly what you've spent this month, broken down by category. Our spending card shows you where your money actually goes, with real-time updates as you link new accounts through Plaid.

**Recent activity you care about**

The home screen shows your most recent transactions, not noise. You see merchant names, categories, and amounts in the order they happened. Tap any transaction to dig deeper.

**Transactions the way you use them**

Stop scrolling through lists of transactions you don't care about. Filter by category, date range, or account. Search for merchants. See exactly what's happening in your financial data, fast.

**Receipt and invoice capture**

Take a photo of a receipt, and Cently's AI reads it instantly. No typing. No manual entry. Just point and capture. Your receipts automatically link to related transactions and help you track business expenses or personal spending.

**Chat with your data**

Ask your financial data questions in plain English. "How much did I spend on groceries last month?" "Show me my biggest transactions this week." "When did I last eat out?" Our AI understands your financial context and gives you answers, not data dumps.

**Plaid-connected accounts**

Link your bank accounts directly to Cently through Plaid. Your transactions sync automatically. You're not manually uploading CSVs on mobile. Your data just flows in.

## The iOS app is built for momentum

Speed matters on mobile. Spend categories load in milliseconds. Your dashboard refreshes instantly. Tap a transaction and see everything connected to it immediately.

We also thought about battery life and data usage. The app doesn't constantly poll your accounts. It's intelligent about background sync. Everything's cached smartly so you can browse your transactions even if you're offline.

## What makes this different from other mobile finance apps

Most finance apps either show you raw data (overwhelming) or offer shallow insights (useless). Cently does both: raw data when you need to dig in, powerful insights when you need to understand your spending patterns.

We also didn't compromise on AI features for mobile. Your iPhone has the same conversational AI interface as the web app. Ask questions. Get answers. It's that simple.

And unlike apps that disappear when you don't check them, Cently connects to your real accounts through Plaid. Your transactions update automatically. Open the app three weeks later and you're not staring at stale data.

## How to get it

The Cently iOS app is available now on the App Store. If you're already using Cently on the web, you can sign in with the same account. Your data syncs across both apps instantly.

New to Cently? Download the app, link your accounts through Plaid, and start seeing your financial data clearly.

## What's next

We built iOS to feel like a first-class experience, not a mobile version. We're continuing to add features like budget tracking, spending forecasts, and receipt organization.

We're also working on something we think will change how you stay aware of your finances. More on that soon.

And we're listening to how you use the app. Every interaction tells us something about what matters in your financial life. That feedback shapes what we build next.

Your finances are too important to be stuck behind a desk. Download Cently for iOS today.

[Download on the App Store →](https://apps.apple.com/app/cently)
